import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import IconButton from "@mui/material/IconButton"
import MenuItem from "@mui/material/MenuItem"
import Paper from "@mui/material/Paper"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi, podsApi } from "../axios"
import AdminPageHeader from "../components/AdminPageHeader"
import PodLiveBoard from "./PodLiveBoard"
import {
  MODE_LABELS,
  POD_TIMEZONES,
  WEEKDAYS,
  campaignRows,
  clientLabel,
  formatTime,
  formatWeekdays,
} from "./podLabels"

function apiError(err, fallback) {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data.detail === "string") return data.detail
  const first = Object.values(data)[0]
  return Array.isArray(first) ? String(first[0]) : fallback
}

function Section({ title, subtitle, actions, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Row
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
      >
        <Column gap={0.25}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Column>
        {actions}
      </Row>
      {children}
    </Paper>
  )
}

function SettingsSection({ pod, onSave }) {
  const [form, setForm] = useState(pod)
  useEffect(() => setForm(pod), [pod])
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <Section
      title="Dialing"
      actions={
        <Button
          variant="contained"
          size="small"
          onClick={() =>
            onSave({
              mode: form.mode,
              max_ratio: form.max_ratio,
              wrap_up_seconds: form.wrap_up_seconds,
              max_attempts: form.max_attempts,
              retry_after_minutes: form.retry_after_minutes,
              active: form.active,
            })
          }
        >
          Save
        </Button>
      }
    >
      <Row sx={{ gap: 2, flexWrap: "wrap" }}>
        <TextField
          select
          size="small"
          label="Mode"
          value={form.mode}
          onChange={set("mode")}
          sx={{ minWidth: 240 }}
        >
          {Object.entries(MODE_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label="Max lines per agent"
          value={form.max_ratio}
          onChange={set("max_ratio")}
          inputProps={{ min: 1, max: 3, step: 0.5 }}
          disabled={form.mode !== "predictive"}
          helperText="Ceiling for predictive pacing (1–3)"
        />
        <TextField
          size="small"
          type="number"
          label="Wrap-up seconds"
          value={form.wrap_up_seconds}
          onChange={set("wrap_up_seconds")}
        />
        <TextField
          size="small"
          type="number"
          label="Attempts per lead"
          value={form.max_attempts}
          onChange={set("max_attempts")}
        />
        <TextField
          size="small"
          type="number"
          label="Retry after (minutes)"
          value={form.retry_after_minutes}
          onChange={set("retry_after_minutes")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.active)}
              onChange={(e) =>
                setForm((f) => ({ ...f, active: e.target.checked }))
              }
            />
          }
          label="Pod active"
        />
      </Row>
    </Section>
  )
}

function AgentsSection({ pod, onChange, setError }) {
  const [employees, setEmployees] = useState([])
  const [userId, setUserId] = useState("")
  const activeMembers = pod.members.filter((m) => m.active)
  const memberIds = new Set(activeMembers.map((m) => m.user.id))

  useEffect(() => {
    podsApi
      .employees()
      .then(({ data }) => setEmployees(data || []))
      .catch(() => setEmployees([]))
  }, [])

  function handleAdd() {
    podsApi
      .addMember(pod.id, userId)
      .then(() => {
        setUserId("")
        onChange()
      })
      .catch((err) => setError(apiError(err, "Could not add the agent.")))
  }

  return (
    <Section
      title="Agents"
      subtitle="Texting-only employees. They only click Ready / Pause; the schedule decides who they call."
    >
      <Column gap={1}>
        {activeMembers.map((member) => (
          <Row
            key={member.id}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="body2">{clientLabel(member.user)}</Typography>
            <IconButton
              size="small"
              aria-label={`Remove ${member.user.email}`}
              onClick={() =>
                podsApi
                  .removeMember(pod.id, member.id)
                  .then(onChange)
                  .catch((err) =>
                    setError(apiError(err, "Could not remove the agent."))
                  )
              }
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Row>
        ))}
        {activeMembers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No agents yet.
          </Typography>
        ) : null}
        <Row sx={{ gap: 1, mt: 1 }}>
          <TextField
            select
            size="small"
            label="Add agent"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ minWidth: 320 }}
          >
            {employees
              .filter((e) => !memberIds.has(e.id))
              .map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  {clientLabel(e)}
                </MenuItem>
              ))}
          </TextField>
          <Button variant="outlined" disabled={!userId} onClick={handleAdd}>
            Add
          </Button>
        </Row>
      </Column>
    </Section>
  )
}

const EMPTY_GOALS = {
  name: "",
  share: 1,
  goal_bookings: "",
  goal_hours: "",
  month_start_day: 1,
}

function goalsPayload(form) {
  return {
    share: Number(form.share) || 1,
    goal_bookings:
      form.goal_bookings === "" ? null : Number(form.goal_bookings),
    goal_hours: form.goal_hours === "" ? null : form.goal_hours,
    month_start_day: Number(form.month_start_day) || 1,
  }
}

function GoalFields({ form, setForm }) {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Monthly goal, worked as 4 weekly targets. The pod stops calling for it
        when it hits either goal for the week, and picks back up next week.
        Leave one blank to use only the other.
      </Typography>
      <Row sx={{ gap: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          type="number"
          label="Bookings / month"
          value={form.goal_bookings}
          onChange={set("goal_bookings")}
        />
        <TextField
          size="small"
          type="number"
          label="Hours / month"
          value={form.goal_hours}
          onChange={set("goal_hours")}
        />
        <TextField
          size="small"
          type="number"
          label="Month starts on day"
          value={form.month_start_day}
          onChange={set("month_start_day")}
          inputProps={{ min: 1, max: 28 }}
        />
        <TextField
          size="small"
          type="number"
          label="Share"
          value={form.share}
          onChange={set("share")}
          inputProps={{ min: 1 }}
          helperText="Weight vs. the pod's other campaigns"
        />
      </Row>
    </>
  )
}

/** Add a client account; its first campaign is created with these goals. */
function ClientDialog({ open, onClose, onSave }) {
  const [form, setForm] = useState({ ...EMPTY_GOALS, account: null })
  const [options, setOptions] = useState([])
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!open) return
    setForm({ ...EMPTY_GOALS, account: null })
    setQuery("")
  }, [open])

  useEffect(() => {
    if (!open || query.length < 2) {
      setOptions([])
      return undefined
    }
    const timer = setTimeout(() => {
      adminApi
        .searchAccounts(query)
        .then(({ data }) => setOptions(data || []))
        .catch(() => setOptions([]))
    }, 300)
    return () => clearTimeout(timer)
  }, [open, query])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add client</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        <Autocomplete
          options={options}
          value={form.account}
          inputValue={query}
          onChange={(_, value) => setForm((f) => ({ ...f, account: value }))}
          onInputChange={(_, value) => setQuery(value)}
          getOptionLabel={(option) => clientLabel(option)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(x) => x}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client account"
              helperText="Search by name or email"
              sx={{ mt: 1 }}
            />
          )}
        />
        <GoalFields form={form} setForm={setForm} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.account}
          onClick={() =>
            onSave({ client_id: form.account.id, ...goalsPayload(form) })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Add or edit a campaign. ``askName`` is off when editing a client's only
 * campaign, so single-campaign clients never have to think about campaigns.
 */
function CampaignDialog({ open, title, campaign, askName, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_GOALS)

  useEffect(() => {
    if (!open) return
    setForm(
      campaign
        ? {
            name: campaign.name,
            share: campaign.share,
            goal_bookings: campaign.goal_bookings ?? "",
            goal_hours: campaign.goal_hours ?? "",
            month_start_day: campaign.month_start_day,
          }
        : EMPTY_GOALS
    )
  }, [open, campaign])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        {askName ? (
          <TextField
            label="Campaign name"
            helperText="e.g. the area it covers"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
        ) : null}
        <GoalFields form={form} setForm={setForm} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={askName && !form.name.trim()}
          onClick={() =>
            onSave({
              ...(askName ? { name: form.name.trim() } : {}),
              ...goalsPayload(form),
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function NumbersCell({ podId, campaign, title, setError }) {
  const [numbers, setNumbers] = useState([])
  const [open, setOpen] = useState(false)
  const [areaCode, setAreaCode] = useState("")
  const [count, setCount] = useState(3)
  const [isBuying, setIsBuying] = useState(false)

  const load = useCallback(
    () =>
      podsApi
        .numbers(podId, campaign.id)
        .then(({ data }) => setNumbers(data || []))
        .catch(() => setNumbers([])),
    [podId, campaign.id]
  )

  useEffect(() => {
    load()
  }, [load])

  function handleBuy() {
    setIsBuying(true)
    podsApi
      .buyNumbers(podId, campaign.id, {
        area_code: areaCode,
        count: Number(count),
      })
      .then(() => {
        setOpen(false)
        load()
      })
      .catch((err) => setError(apiError(err, "Could not buy numbers.")))
      .finally(() => setIsBuying(false))
  }

  function makeTextNumber(number) {
    podsApi
      .setTextNumber(podId, campaign.id, number.id)
      .then(({ data }) => setNumbers(data || []))
      .catch((err) => setError(apiError(err, "Could not switch numbers.")))
  }

  const textNumber = numbers.find((n) => n.is_text_number)

  return (
    <>
      <Typography
        variant="body2"
        title={numbers.map((n) => n.phone).join(", ")}
      >
        {numbers.length}
        {textNumber ? ` · texts from ${textNumber.phone}` : ""}{" "}
        <Button size="small" onClick={() => setOpen(true)}>
          Manage
        </Button>
      </Typography>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Numbers for {title}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Calls rotate across the block (each lead always sees the same
            number). All texts go out from the one texting number, and replies
            to any number land in the same pod inbox thread. About $1.15 a month
            each; new numbers join the 10DLC campaign.
          </Typography>
          {numbers.length ? (
            <RadioGroup
              aria-label="Texting number"
              value={textNumber ? String(textNumber.id) : ""}
              onChange={(e) =>
                makeTextNumber(
                  numbers.find((n) => String(n.id) === e.target.value)
                )
              }
            >
              {numbers.map((number) => (
                <FormControlLabel
                  key={number.id}
                  value={String(number.id)}
                  control={<Radio size="small" />}
                  label={
                    number.is_text_number
                      ? `${number.phone} — texting number`
                      : number.phone
                  }
                />
              ))}
            </RadioGroup>
          ) : null}
          <Typography variant="subtitle2">Buy more</Typography>
          <Row sx={{ gap: 2 }}>
            <TextField
              size="small"
              label="Area code"
              value={areaCode}
              onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
              inputProps={{ maxLength: 3 }}
            />
            <TextField
              size="small"
              type="number"
              label="How many"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              inputProps={{ min: 1, max: 6 }}
            />
          </Row>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={areaCode.length !== 3 || isBuying}
            onClick={handleBuy}
          >
            Buy
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function CampaignCells({ pod, campaign, title, summary, setError }) {
  return (
    <>
      <TableCell align="right">{campaign.goal_bookings ?? "—"}</TableCell>
      <TableCell align="right">{campaign.goal_hours ?? "—"}</TableCell>
      <TableCell align="right">{campaign.share}</TableCell>
      <TableCell align="right">
        {summary[campaign.id]?.pending ?? campaign.pending_leads}
      </TableCell>
      <TableCell align="right">{summary[campaign.id]?.done ?? 0}</TableCell>
      <TableCell>
        <NumbersCell
          podId={pod.id}
          campaign={campaign}
          title={title}
          setError={setError}
        />
      </TableCell>
    </>
  )
}

export function ClientsSection({ pod, onChange, setError }) {
  const [addClientOpen, setAddClientOpen] = useState(false)
  // {podClient, campaign (null = new), askName, title}
  const [campaignDialog, setCampaignDialog] = useState(null)
  const [summary, setSummary] = useState({})
  const activeClients = pod.clients.filter((c) => c.active)

  useEffect(() => {
    podsApi
      .leadSummary(pod.id)
      .then(({ data }) =>
        setSummary(
          Object.fromEntries((data || []).map((row) => [row.campaign_id, row]))
        )
      )
      .catch(() => setSummary({}))
  }, [pod])

  function run(request, message) {
    return request
      .then(() => {
        setAddClientOpen(false)
        setCampaignDialog(null)
        onChange()
      })
      .catch((err) => setError(apiError(err, message)))
  }

  function saveCampaign(payload) {
    const { podClient, campaign } = campaignDialog
    run(
      campaign
        ? podsApi.updateCampaign(pod.id, campaign.id, payload)
        : podsApi.addCampaign(pod.id, podClient.id, payload),
      "Could not save the campaign."
    )
  }

  function removeClient(podClient) {
    run(
      podsApi.removeClient(pod.id, podClient.id),
      "Could not remove the client."
    )
  }

  function removeCampaign(campaign) {
    run(
      podsApi.removeCampaign(pod.id, campaign.id),
      "Could not remove the campaign."
    )
  }

  function addCampaignButton(podClient) {
    return (
      <IconButton
        size="small"
        aria-label={`Add campaign for ${clientLabel(podClient.client)}`}
        title="Add campaign"
        onClick={() =>
          setCampaignDialog({
            podClient,
            campaign: null,
            askName: true,
            title: `New campaign for ${clientLabel(podClient.client)}`,
          })
        }
      >
        <AddIcon fontSize="small" />
      </IconButton>
    )
  }

  return (
    <Section
      title="Clients"
      subtitle="Leads are added from each client's own leads pages with “Assign to pod”. Add a campaign to a client to split it, e.g. by area."
      actions={
        <Button
          variant="outlined"
          size="small"
          onClick={() => setAddClientOpen(true)}
        >
          Add client
        </Button>
      }
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Client</TableCell>
            <TableCell align="right">Bookings / mo</TableCell>
            <TableCell align="right">Hours / mo</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Leads waiting</TableCell>
            <TableCell align="right">Done</TableCell>
            <TableCell>Numbers</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {activeClients.map((podClient) => {
            const campaigns = podClient.campaigns || []
            const label = clientLabel(podClient.client)
            if (campaigns.length <= 1) {
              const campaign = campaigns[0]
              return (
                <TableRow key={podClient.id}>
                  <TableCell>{label}</TableCell>
                  {campaign ? (
                    <CampaignCells
                      pod={pod}
                      campaign={campaign}
                      title={label}
                      summary={summary}
                      setError={setError}
                    />
                  ) : (
                    <TableCell colSpan={6} />
                  )}
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {campaign ? (
                      <IconButton
                        size="small"
                        aria-label="Edit client"
                        onClick={() =>
                          setCampaignDialog({
                            podClient,
                            campaign,
                            askName: false,
                            title: `Edit ${label}`,
                          })
                        }
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    {addCampaignButton(podClient)}
                    <IconButton
                      size="small"
                      aria-label="Remove client"
                      onClick={() => removeClient(podClient)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            }
            return [
              <TableRow key={`client-${podClient.id}`}>
                <TableCell colSpan={7} sx={{ fontWeight: 600 }}>
                  {label}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {addCampaignButton(podClient)}
                  <IconButton
                    size="small"
                    aria-label="Remove client"
                    onClick={() => removeClient(podClient)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>,
              ...campaigns.map((campaign) => (
                <TableRow key={`campaign-${campaign.id}`}>
                  <TableCell sx={{ pl: 4 }}>{campaign.name}</TableCell>
                  <CampaignCells
                    pod={pod}
                    campaign={campaign}
                    title={`${label} — ${campaign.name}`}
                    summary={summary}
                    setError={setError}
                  />
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      aria-label={`Edit campaign ${campaign.name}`}
                      onClick={() =>
                        setCampaignDialog({
                          podClient,
                          campaign,
                          askName: true,
                          title: `Edit ${campaign.name}`,
                        })
                      }
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Remove campaign ${campaign.name}`}
                      onClick={() => removeCampaign(campaign)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )),
            ]
          })}
          {activeClients.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary">
                  No clients yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <ClientDialog
        open={addClientOpen}
        onClose={() => setAddClientOpen(false)}
        onSave={(payload) =>
          run(podsApi.addClient(pod.id, payload), "Could not add the client.")
        }
      />
      <CampaignDialog
        open={Boolean(campaignDialog)}
        title={campaignDialog?.title || ""}
        campaign={campaignDialog?.campaign || null}
        askName={Boolean(campaignDialog?.askName)}
        onClose={() => setCampaignDialog(null)}
        onSave={saveCampaign}
      />
    </Section>
  )
}

const EMPTY_BLOCK = {
  weekdays: [0, 1, 2, 3, 4],
  start_time: "09:00",
  end_time: "12:00",
  timezone: "America/New_York",
  campaign_shares: {},
}

function ScheduleSection({ pod, onChange, setError }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_BLOCK)
  const rows = campaignRows(pod)
  const labelsById = Object.fromEntries(
    rows.map((row) => [String(row.campaign.id), row.label])
  )

  function toggleDay(day) {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(day)
        ? f.weekdays.filter((d) => d !== day)
        : [...f.weekdays, day],
    }))
  }

  function setShare(campaignId, value) {
    setForm((f) => {
      const shares = { ...f.campaign_shares }
      if (value === "") delete shares[campaignId]
      else shares[campaignId] = Math.max(0, Number(value) || 0)
      return { ...f, campaign_shares: shares }
    })
  }

  function handleAdd() {
    podsApi
      .addBlock(pod.id, form)
      .then(() => {
        setOpen(false)
        setForm(EMPTY_BLOCK)
        onChange()
      })
      .catch((err) => setError(apiError(err, "Could not add the block.")))
  }

  function describeShares(block) {
    const entries = Object.entries(block.campaign_shares || {})
    if (entries.length === 0) return "All clients at their share"
    return entries
      .filter(([, weight]) => weight > 0)
      .map(([id, weight]) => `${labelsById[id] || `#${id}`} ×${weight}`)
      .join(", ")
  }

  return (
    <Section
      title="Schedule"
      subtitle="The pod dials automatically inside these blocks and stops at the end."
      actions={
        <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
          Add block
        </Button>
      }
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Days</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Clients</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {pod.schedule_blocks.map((block) => (
            <TableRow key={block.id}>
              <TableCell>{formatWeekdays(block.weekdays)}</TableCell>
              <TableCell>
                {formatTime(block.start_time)}–{formatTime(block.end_time)}{" "}
                {block.timezone.replace("America/", "").replace("_", " ")}
              </TableCell>
              <TableCell>{describeShares(block)}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Remove block"
                  onClick={() =>
                    podsApi
                      .removeBlock(pod.id, block.id)
                      .then(onChange)
                      .catch((err) =>
                        setError(apiError(err, "Could not remove the block."))
                      )
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {pod.schedule_blocks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">
                  No schedule yet — the pod won’t dial until it has one.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add schedule block</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Row sx={{ flexWrap: "wrap", mt: 1 }}>
            {WEEKDAYS.map((label, day) => (
              <FormControlLabel
                key={label}
                control={
                  <Checkbox
                    size="small"
                    checked={form.weekdays.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                }
                label={label}
              />
            ))}
          </Row>
          <Row sx={{ gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              type="time"
              label="Start"
              value={form.start_time}
              onChange={(e) =>
                setForm((f) => ({ ...f, start_time: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="time"
              label="End"
              value={form.end_time}
              onChange={(e) =>
                setForm((f) => ({ ...f, end_time: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              size="small"
              label="Timezone"
              value={form.timezone}
              onChange={(e) =>
                setForm((f) => ({ ...f, timezone: e.target.value }))
              }
              sx={{ minWidth: 200 }}
            >
              {POD_TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </TextField>
          </Row>
          <Typography variant="body2" color="text.secondary">
            Mix for this block. Leave all blank to call every client at its own
            share; set 0 to leave one out of this block.
          </Typography>
          {rows.map(({ campaign, label }) => (
            <Row key={campaign.id} sx={{ alignItems: "center", gap: 2 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {label}
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Weight"
                value={form.campaign_shares[campaign.id] ?? ""}
                onChange={(e) => setShare(campaign.id, e.target.value)}
                sx={{ width: 110 }}
              />
            </Row>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={form.weekdays.length === 0}
            onClick={handleAdd}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  )
}

export default function AdminPodDetail() {
  const { podId } = useParams()
  const [pod, setPod] = useState(null)
  const [error, setError] = useState("")
  const [saved, setSaved] = useState(false)

  const load = useCallback(
    () =>
      podsApi
        .get(podId)
        .then(({ data }) => setPod(data))
        .catch(() => setError("Could not load the pod.")),
    [podId]
  )

  useEffect(() => {
    load()
  }, [load])

  function handleSaveSettings(payload) {
    setSaved(false)
    podsApi
      .update(podId, payload)
      .then(({ data }) => {
        setPod(data)
        setSaved(true)
      })
      .catch((err) => setError(apiError(err, "Could not save settings.")))
  }

  if (!pod) {
    return error ? <Alert severity="error">{error}</Alert> : null
  }

  return (
    <Column gap={3}>
      <AdminPageHeader title={pod.name} subtitle="Pod setup" />
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" onClose={() => setSaved(false)}>
          Saved.
        </Alert>
      ) : null}
      <PodLiveBoard podId={pod.id} />
      <SettingsSection pod={pod} onSave={handleSaveSettings} />
      <AgentsSection pod={pod} onChange={load} setError={setError} />
      <ClientsSection pod={pod} onChange={load} setError={setError} />
      <ScheduleSection pod={pod} onChange={load} setError={setError} />
    </Column>
  )
}
